// The front end's half of the push-event system. One timer, shared by the whole app, polls
// /api/push-events every 5 seconds and re-broadcasts whatever came back on $rootScope. Nothing
// else in the app should poll that endpoint - consumers listen for a broadcast instead.
//
// An event only *names* what changed (a type plus a few ids); the listener re-fetches the detail
// it needs. That is what keeps the poll cheap enough to run at this interval.
app.service("PushEventService", [
	"$rootScope",
	"$interval",
	"$document",
	"PushEventAPI",
	"Session",
	"PUSH_EVENTS",
	"LiveUpdateGuard",
	function ($rootScope, $interval, $document, PushEventAPI, Session, PUSH_EVENTS, LiveUpdateGuard) {
		// The poll cadence. The backend poll is a cursor read per channel, so an idle poll costs the
		// read floor and this stays affordable.
		const POLL_INTERVAL_MS = 5000;

		// How many ticks to skip after consecutive failures, so a backend outage backs off to roughly
		// one attempt a minute instead of hammering at 5-second intervals. The last value repeats.
		const BACKOFF_TICKS = [1, 2, 4, 8, 12];

		// Failures in a row after which the poll stops altogether, about seven minutes into the backoff
		// above. Long enough to ride out a deploy; short enough that a back end which keeps failing
		// stops hearing from every open tab. Polling starts again on a practice switch, another user
		// logging in on the tab, a re-login, or a page reload.
		const FAILURES_BEFORE_STOP = 10;

		// How many event ids to remember for de-duplication. Cursors already stop the server resending
		// an event, so this only guards the edges (an overlapping poll, a retried request).
		const SEEN_LIMIT = 200;

		let timer = null;
		let inFlight = false;
		let practiceCursor = null;
		let userCursor = null;
		let channelPracticeId = null;
		let channelUserId = null;
		// Bumped whenever the channel is reset, so a response to a poll made for the previous
		// practice, user or session is recognised and dropped when it lands.
		let generation = 0;
		let seenEventIds = [];
		let failureCount = 0;
		let ticksToSkip = 0;
		let halted = false;

		/**
		 * Whether a poll can be made right now. The endpoint needs both a token and a practice
		 * (X-PRACTICE-ID identifies the practice channel), so before a practice is chosen there is
		 * nothing to poll.
		 */
		function canPoll() {
			return !!(Session.token && Session.practice && Session.practice.id);
		}

		/**
		 * Drops the cursors so the next poll starts from now. Called when the channel being polled
		 * changes - cursors are per channel, and a cursor from another practice means nothing here.
		 * The follow-ups stopped for failing are let go too: what failed for one practice or user may
		 * well work for the next.
		 */
		function resetCursors() {
			generation++;
			practiceCursor = null;
			userCursor = null;
			seenEventIds = [];
			LiveUpdateGuard.reset();
		}

		/** True the first time an event id is offered, false on every repeat. */
		function isNewEvent(eventId) {
			if (!eventId) {
				return true;
			}
			if (seenEventIds.indexOf(eventId) !== -1) {
				return false;
			}
			seenEventIds.push(eventId);
			if (seenEventIds.length > SEEN_LIMIT) {
				seenEventIds = seenEventIds.slice(-SEEN_LIMIT);
			}
			return true;
		}

		/**
		 * The event's payload as an object. It arrives as a JSON string naming the ids that changed;
		 * a malformed one must not take the poller down, so it degrades to an empty object.
		 */
		function parsePayload(payload) {
			if (!payload) {
				return {};
			}
			try {
				return angular.fromJson(payload);
			} catch (e) {
				return {};
			}
		}

		/**
		 * Re-broadcasts one event on $rootScope, twice: once under its own type so a listener can
		 * subscribe narrowly, and once under a catch-all for anything that wants the whole stream.
		 *
		 * Listen for a specific type:
		 *   $scope.$on(PUSH_EVENTS.INVOICE_PAID, function (e, event) { ... event.data.invoiceId ... });
		 * or for everything:
		 *   $scope.$on(PUSH_EVENTS.ANY, function (e, event) { ... event.type ... });
		 */
		function broadcast(event) {
			const broadcastEvent = {
				id: event.event_id,
				type: event.event_type,
				channel: event.channel,
				dateCreated: event.date_created,
				data: parsePayload(event.payload),
			};

			if (broadcastEvent.type) {
				$rootScope.$broadcast("pushEvent:" + broadcastEvent.type, broadcastEvent);
			}
			$rootScope.$broadcast(PUSH_EVENTS.ANY, broadcastEvent);
		}

		/** One poll: fetch, store the fresh cursors, broadcast whatever is new. */
		function poll() {
			// A practice switch changes the channel, so start that channel from now rather than
			// replaying the previous practice's events into the new one.
			// Likewise a different user on the same tab: the user channel and its cursor are theirs.
			if (channelPracticeId !== Session.practice.id || channelUserId !== Session.userId) {
				channelPracticeId = Session.practice.id;
				channelUserId = Session.userId;
				resetCursors();
			}

			const requestGeneration = generation;
			inFlight = true;
			PushEventAPI.pollEvents(practiceCursor, userCursor, channelPracticeId).then(
				function (resp) {
					if (requestGeneration !== generation) {
						// Polled for a channel that has since been left (practice switch, logout, stop()).
						// Its cursors and events belong to that channel, so neither is applied here, and
						// inFlight now belongs to the current channel's poll.
						return;
					}
					inFlight = false;
					// The user switched practice or logged out while this was in flight. What came back
					// is the old channel's, so it is not broadcast into the new one or kept as its cursor;
					// the next tick resets the channel.
					if (!canPoll() || Session.practice.id !== channelPracticeId || Session.userId !== channelUserId) {
						return;
					}
					failureCount = 0;
					ticksToSkip = 0;

					const data = resp.data || {};

					// Keep the cursors before broadcasting. A listener that throws must not cost us the
					// cursor and make the next poll replay the same events.
					practiceCursor = data.practice_cursor || practiceCursor;
					userCursor = data.user_cursor || userCursor;

					// Events arrive oldest first; broadcast them in that order.
					(data.events || []).forEach(function (event) {
						if (isNewEvent(event.event_id)) {
							broadcast(event);
						}
					});
				},
				function () {
					if (requestGeneration !== generation) {
						return;
					}
					// Deliberately silent: a failed poll is not actionable by the user, and the response
					// can carry patient ids, so it is not logged either. Back off and try again, or give up
					// once it has failed too often in a row.
					inFlight = false;
					failureCount++;
					ticksToSkip = BACKOFF_TICKS[Math.min(failureCount - 1, BACKOFF_TICKS.length - 1)];
					if (failureCount >= FAILURES_BEFORE_STOP) {
						halted = true;
					}
				}
			);
		}

		/** Decides, each tick, whether this tick should actually poll. */
		function tick() {
			if (inFlight) {
				return;
			}

			if (!canPoll()) {
				// The session has gone (logout, or a session timeout). Drop the cursors so that logging
				// back in starts from now instead of replaying whatever happened in between.
				if (channelPracticeId !== null) {
					channelPracticeId = null;
					channelUserId = null;
					resetCursors();
				}
				return;
			}

			// Nothing on a hidden tab can be looking at a stale view. Cursors are held across the gap,
			// so the first poll after it becomes visible again catches up on everything missed.
			if ($document[0] && $document[0].hidden) {
				return;
			}

			if (halted) {
				// Stopped for failing. A different practice or user is a different channel and gets a
				// fresh start; poll() resets the cursors and the stopped follow-ups for it.
				if (Session.practice.id === channelPracticeId && Session.userId === channelUserId) {
					return;
				}
				halted = false;
				failureCount = 0;
				ticksToSkip = 0;
			}

			if (ticksToSkip > 0) {
				ticksToSkip--;
				return;
			}

			poll();
		}

		const service = {
			/**
			 * Starts the shared poll timer. Idempotent, and safe to call before login - a tick with no
			 * session or no practice costs nothing and makes no request.
			 */
			start: function () {
				if (timer) {
					return;
				}
				// invokeApply false: an idle tick must not force a digest across the whole app every 5
				// seconds. A tick that does poll gets its digest from $http resolving anyway.
				timer = $interval(tick, POLL_INTERVAL_MS, 0, false);

				// Coming back to the tab shouldn't wait out the rest of the interval.
				$document.on("visibilitychange", service.pollNow);
			},

			/** Stops polling and forgets the cursors. Called on logout. */
			stop: function () {
				if (timer) {
					$interval.cancel(timer);
					timer = null;
					$document.off("visibilitychange", service.pollNow);
				}
				inFlight = false;
				failureCount = 0;
				ticksToSkip = 0;
				halted = false;
				channelPracticeId = null;
				channelUserId = null;
				resetCursors();
			},

			/**
			 * Polls immediately instead of waiting for the next tick, clearing any backoff. Use after
			 * an action whose result arrives as an event. Does nothing once the poll has stopped for
			 * failing - coming back to the tab must not restart it.
			 */
			pollNow: function () {
				if (halted) {
					return;
				}
				ticksToSkip = 0;
				if (!inFlight && canPoll() && timer) {
					poll();
				}
			},
		};

		return service;
	},
]);
